import { createContext, useContext, useReducer, ReactNode, FC } from 'react';
import {
  GuardianState,
  GuardianChild,
} from '../guardian.interfaces';

type GuardianAction =
  | { type: 'SET_SELECTED_CHILD'; payload: string | null }
  | { type: 'SET_CHILDREN'; payload: GuardianChild[] }
  | { type: 'RESET' };

const initialState: GuardianState = {
  selectedChildId: null,
  children: [],
};

const guardianReducer = (
  state: GuardianState,
  action: GuardianAction
): GuardianState => {
  switch (action.type) {
    case 'SET_SELECTED_CHILD':
      return {
        ...state,
        selectedChildId: action.payload,
      };
    case 'SET_CHILDREN':
      return {
        ...state,
        children: action.payload,
        selectedChildId:
          action.payload.length === 1 ? action.payload[0].id : null,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

type GuardianContextType = {
  state: GuardianState;
  setSelectedChild: (childId: string | null) => void;
  setChildren: (children: GuardianChild[]) => void;
  reset: () => void;
};

const GuardianContext = createContext<GuardianContextType | undefined>(
  undefined
);

export const GuardianProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(guardianReducer, initialState);

  const setSelectedChild = (childId: string | null) => {
    dispatch({ type: 'SET_SELECTED_CHILD', payload: childId });
  };

  const setChildren = (children: GuardianChild[]) => {
    dispatch({ type: 'SET_CHILDREN', payload: children });
  };

  const reset = () => {
    dispatch({ type: 'RESET' });
  };

  return (
    <GuardianContext.Provider
      value={{
        state,
        setSelectedChild,
        setChildren,
        reset,
      }}
    >
      {children}
    </GuardianContext.Provider>
  );
};

export const useGuardianState = () => {
  const context = useContext(GuardianContext);
  if (context === undefined) {
    throw new Error('useGuardianState must be used within a GuardianProvider');
  }
  return context.state;
};

export const useGuardianActions = () => {
  const context = useContext(GuardianContext);
  if (context === undefined) {
    throw new Error('useGuardianActions must be used within a GuardianProvider');
  }
  return {
    setSelectedChild: context.setSelectedChild,
    setChildren: context.setChildren,
    reset: context.reset,
  };
};
